import type {Message} from '@pinecast/i18n';
import {defineMessage} from '@pinecast/i18n';

export const names = {
  aud: defineMessage({
    description: 'Name of currency "aud"',
    defaultMessage: 'AUD - Australian Dollar',
  }),
  bgn: defineMessage({
    description: 'Name of currency "bgn"',
    defaultMessage: 'BGN - Bulgarian Lev',
  }),
  brl: defineMessage({
    description: 'Name of currency "brl"',
    defaultMessage: 'BRL - Brazilian Real',
  }),
  cad: defineMessage({
    description: 'Name of currency "cad"',
    defaultMessage: 'CAD - Canadian Dollar',
  }),
  chf: defineMessage({
    description: 'Name of currency "chf"',
    defaultMessage: 'CHF - Swiss Franc',
  }),
  czk: defineMessage({
    description: 'Name of currency "czk"',
    defaultMessage: 'CZK - Czech Koruna',
  }),
  dkk: defineMessage({
    description: 'Name of currency "dkk"',
    defaultMessage: 'DKK - Danish Krone',
  }),
  eur: defineMessage({
    description: 'Name of currency "eur"',
    defaultMessage: 'EUR - Euro',
  }),
  gbp: defineMessage({
    description: 'Name of currency "gbp"',
    defaultMessage: 'GBP - Pound Sterling',
  }),
  hkd: defineMessage({
    description: 'Name of currency "hkd"',
    defaultMessage: 'HKD - Hong Kong Dollar',
  }),
  huf: defineMessage({
    description: 'Name of currency "huf"',
    defaultMessage: 'HUF - Hungarian Forint',
  }),
  jpy: defineMessage({
    description: 'Name of currency "jpy"',
    defaultMessage: 'JPY - Japanese Yen',
  }),
  nok: defineMessage({
    description: 'Name of currency "nok"',
    defaultMessage: 'NOK - Norwegian Krone',
  }),
  nzd: defineMessage({
    description: 'Name of currency "nzd"',
    defaultMessage: 'NZD - New Zealand Dollar',
  }),
  ron: defineMessage({
    description: 'Name of currency "ron"',
    defaultMessage: 'RON - Romanian Leu',
  }),
  pln: defineMessage({
    description: 'Name of currency "pln"',
    defaultMessage: 'PLN - Polish Złoty',
  }),
  sek: defineMessage({
    description: 'Name of currency "sek"',
    defaultMessage: 'SEK - Swedish Krone',
  }),
  sgd: defineMessage({
    description: 'Name of currency "sgd"',
    defaultMessage: 'SGD - Singapore Dollar',
  }),
  usd: defineMessage({
    description: 'Name of currency "usd"',
    defaultMessage: 'USD - US Dollar',
  }),
} as Record<string, Message>;

const europeanMap = {
  eur: [
    'AT',
    'BE',
    'CH',
    'DE',
    'DK',
    'ES',
    'FI',
    'FR',
    'GB',
    'IE',
    'IT',
    'LU',
    'NL',
    'NO',
    'PT',
    'SE',
  ],
  dkk: ['DK'],
  gbp: ['GB'],
  nok: ['NO'],
  sek: ['SE'],
  usd: ['US'],
  chf: ['CH'],
};

export const countriesToCurrencies = {
  AT: {...europeanMap},
  AU: {aud: ['AU']},
  BE: {...europeanMap},
  BG: {...europeanMap, bgn: ['BG']},
  CA: {cad: ['CA'], usd: ['CA', 'US']},
  CH: {...europeanMap},
  CY: {...europeanMap},
  CZ: {...europeanMap, czk: ['CZ']},
  DE: {...europeanMap},
  DK: {...europeanMap},
  EE: {...europeanMap},
  ES: {...europeanMap},
  FI: {...europeanMap},
  FR: {...europeanMap},
  GB: {...europeanMap},
  GR: {...europeanMap},
  HK: {hkd: ['HK']},
  HU: {...europeanMap, huf: ['HU']},
  IE: {...europeanMap},
  IT: {...europeanMap},
  JP: {jpy: ['JP']},
  LT: {...europeanMap},
  LU: {...europeanMap},
  LV: {...europeanMap},
  MT: {...europeanMap},
  NL: {...europeanMap},
  NO: {...europeanMap},
  NZ: {nzd: ['NZ']},
  PL: {...europeanMap, pln: ['PL']},
  PT: {...europeanMap},
  RO: {...europeanMap, ron: ['RO']},
  SE: {...europeanMap},
  SG: {sgd: ['SG']},
  SI: {...europeanMap},
  SK: {...europeanMap},
  US: {usd: ['US']},
};

export const defaultCurrency: {
  [country in keyof typeof countriesToCurrencies]: keyof (typeof countriesToCurrencies)[country];
} = {
  AT: 'eur',
  AU: 'aud',
  BE: 'eur',
  BG: 'eur',
  CA: 'cad',
  CH: 'chf',
  CY: 'eur',
  CZ: 'czk',
  DE: 'eur',
  DK: 'dkk',
  EE: 'eur',
  ES: 'eur',
  FI: 'eur',
  FR: 'eur',
  GB: 'eur',
  GR: 'eur',
  HK: 'hkd',
  HU: 'huf',
  IE: 'eur',
  IT: 'eur',
  JP: 'jpy',
  LT: 'eur',
  LU: 'eur',
  LV: 'eur',
  MT: 'eur',
  NL: 'eur',
  NO: 'nok',
  NZ: 'nzd',
  PL: 'pln',
  PT: 'eur',
  RO: 'ron',
  SE: 'sek',
  SG: 'sgd',
  SI: 'eur',
  SK: 'eur',
  US: 'usd',
};

// Keep this in sync with tip_jar_currencies.py
export const amounts: Record<
  string,
  [number, number, number, number, number, number, number, number]
> = {
  usd: [100, 300, 500, 1000, 2000, 5000, 10000, 15000],

  brl: [500, 1500, 2500, 5000, 10000, 25000, 50000, 75000],
  czk: [2000, 6000, 10000, 20000, 40000, 100000, 200000, 300000],
  dkk: [500, 1500, 2500, 5000, 10000, 25000, 50000, 100000],
  hkd: [500, 1500, 2500, 5000, 10000, 25000, 50000, 100000],
  huf: [30000, 90000, 150000, 300000, 600000, 1500000, 3000000, 4500000],
  jpy: [10000, 30000, 50000, 100000, 200000, 500000, 1000000, 1500000],
  nok: [1000, 3000, 5000, 10000, 20000, 50000, 100000, 150000],
  ron: [400, 1200, 2000, 4000, 8000, 20000, 40000, 60000],
  pln: [400, 1200, 2000, 4000, 8000, 20000, 40000, 60000],
  sek: [500, 1500, 2500, 5000, 10000, 25000, 50000, 75000],
};
